import React from "react";
import { Link } from "react-router-dom";
import { MapPin, BedDouble, Bath, Maximize, Gauge, Fuel, Briefcase, Star, Lock, ArrowUpRight, BadgeCheck } from "lucide-react";
import { categoryById, slugify } from "../lib/data";
import { resolveImage } from "../lib/api";
import { RatingBadge } from "./ReviewsBlock";

const PLACEHOLDER = "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?crop=entropy&cs=srgb&fm=jpg&q=80&w=800";

function keySummary(listing) {
  const kd = listing.keyDetails || {};
  const out = [];
  if (listing.category === "properties") {
    if (kd.bhk) out.push({ icon: BedDouble, text: `${kd.bhk} BHK` });
    else if (kd.bedrooms) out.push({ icon: BedDouble, text: `${kd.bedrooms} Beds` });
    if (kd.bathrooms) out.push({ icon: Bath, text: `${kd.bathrooms} Baths` });
    if (kd.area_sqft) {
      // Show the exact unit the user selected — never convert or default.
      const unit = kd.area_unit && String(kd.area_unit).trim() ? kd.area_unit : "";
      out.push({ icon: Maximize, text: unit ? `${kd.area_sqft} ${unit}` : String(kd.area_sqft) });
    }
  } else if (listing.category === "vehicles") {
    if (kd.year) out.push({ icon: Star, text: `${kd.year}` });
    if (kd.km_driven) out.push({ icon: Gauge, text: `${Number(kd.km_driven).toLocaleString("en-IN")} km` });
    if (kd.fuel_type) out.push({ icon: Fuel, text: kd.fuel_type });
  } else {
    if (kd.experience_years) out.push({ icon: Briefcase, text: `${kd.experience_years} yrs exp` });
    if (kd.service_type) out.push({ icon: Star, text: kd.service_type });
  }
  return out.slice(0, 3);
}

// Deep forest green — single accent
const GREEN = "#14532D";

export default function ListingCard({ listing }) {
  const cat = categoryById(listing.category);
  const img = resolveImage(listing.images && listing.images[0]) || PLACEHOLDER;
  const details = keySummary(listing);
  const href = `/listings/${listing.id}/${listing.slug || slugify(listing.title)}`;
  const alt = `${listing.title} - ${cat ? cat.label : listing.category} in ${listing.area || ""} ${listing.city}`.trim();
  const isVerified = !!(listing.verified_badge || listing.isVerifiedSeller);

  // Golden theme tokens for verified sellers (subtle, professional)
  const cardCls = isVerified
    ? "group bg-[#FFFBEB] rounded-2xl border-2 border-[#D4AF37] overflow-hidden transition-all duration-300 hover:-translate-y-1 shadow-[0_2px_10px_rgba(184,134,11,0.15)] hover:shadow-[0_18px_40px_rgba(184,134,11,0.22)]"
    : "group bg-white rounded-2xl border border-neutral-200 overflow-hidden transition-all duration-300 hover:-translate-y-1 shadow-[0_2px_10px_rgba(0,0,0,0.04)] hover:shadow-[0_18px_40px_rgba(0,0,0,0.10)] hover:border-neutral-900/20";

  return (
    <div
      className={cardCls}
      data-testid={`listing-card-${listing.id}`}
      data-verified={isVerified ? "true" : undefined}
    >
      {/* Image */}
      <Link to={href} className="block relative overflow-hidden">
        <div className="relative aspect-[4/3] bg-neutral-100">
          <img src={img} alt={alt} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-[600ms] ease-out" />
          {/* Subtle bottom vignette for premium feel */}
          <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/25 to-transparent pointer-events-none" />

          <span className="absolute top-3 left-3 bg-white/95 backdrop-blur text-neutral-900 text-[11px] font-semibold px-2.5 py-1 rounded-full shadow-sm">
            {cat ? cat.label : listing.category}
          </span>
          {isVerified && (
            <span
              className="absolute top-3 right-3 inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full shadow-sm"
              style={{ background: "linear-gradient(135deg,#FFD700 0%,#B8860B 100%)", color: "#1a1a1a" }}
              data-testid="verified-badge"
              title="Verified Seller"
            >
              <BadgeCheck className="h-3.5 w-3.5" /> Verified
            </span>
          )}
          {!isVerified && listing.reviewCount > 0 && (
            <RatingBadge average={listing.reviewAverage} count={listing.reviewCount} className="absolute top-3 right-3" />
          )}
        </div>
      </Link>

      {/* Body */}
      <div className="p-5">
        {/* 1. Title */}
        <Link to={href}>
          <h3 className="font-display font-semibold text-[15px] leading-snug text-neutral-900 line-clamp-2 min-h-[2.4rem] group-hover:text-[color:var(--primary)] transition-colors">
            {listing.title}
          </h3>
        </Link>

        {/* 2. Price + Negotiable badge */}
        <div className="mt-2 flex items-center justify-between gap-2">
          <span className="font-display font-extrabold text-[22px] leading-none tracking-tight text-neutral-900">
            {listing.priceLabel || "Contact for Price"}
          </span>
          {listing.priceType === "negotiable" && (
            <span
              className="text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full border"
              style={{ borderColor: GREEN, color: GREEN, backgroundColor: "#F0FDF4" }}
            >
              Negotiable
            </span>
          )}
        </div>

        {/* 3. Location */}
        <p className="flex items-center gap-1 text-neutral-500 text-xs mt-1.5">
          <MapPin className="h-3.5 w-3.5 shrink-0" /> {listing.area ? `${listing.area}, ` : ""}{listing.city}
        </p>

        {/* 4. Features */}
        {details.length > 0 && (
          <>
            <div className="border-t border-neutral-100 my-4" />
            <div className="flex items-center gap-4 text-xs text-neutral-500 flex-wrap">
              {details.map((d, i) => (
                <span key={i} className="inline-flex items-center gap-1">
                  <d.icon className="h-3.5 w-3.5 text-neutral-500" /> {d.text}
                </span>
              ))}
            </div>
          </>
        )}

        {/* 5. Buttons — Unlock Contact + View */}
        <div className="mt-5 flex items-stretch gap-2">
          <Link
            to={href}
            className="flex-1 inline-flex items-center justify-center gap-1.5 h-10 rounded-full text-xs font-semibold text-white transition-all active:scale-[0.98]"
            style={{ backgroundColor: GREEN }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#166534")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = GREEN)}
            data-testid="card-unlock-btn"
          >
            <Lock className="h-3.5 w-3.5" /> Unlock Contact
          </Link>
          <Link
            to={href}
            className="flex-1 inline-flex items-center justify-center gap-1.5 h-10 rounded-full text-xs font-semibold border border-neutral-900 text-neutral-900 transition-all hover:bg-neutral-900 hover:text-white active:scale-[0.98]"
            data-testid="card-view-btn"
          >
            View <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    </div>
  );
}
